import React, { Fragment, useEffect, useState, useContext } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

import usFlag from '../../assets/flagicons/en.png';
import frFlag from '../../assets/flagicons/fr.png';
import { LanguageSelectorContext } from '../../context/LanguageSelectorContext'; // Import the context

const LanguageSelector: React.FC = () => {
  const selectableLanguages = ['en', 'fr'];

  const languageLabels = {
    en: 'English',
    fr: 'Français',
  };

  const languageFlags = {
    en: usFlag,
    fr: frFlag,
  };

  // Access the context
  const { selectedLanguage: contextLanguage, setSelectedLanguage: setContextLanguage } =
    useContext(LanguageSelectorContext);

  // Local state for the selected language
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const savedLanguage = localStorage.getItem('mrs2-selectedLanguage') ?? 'en';

    // Return the saved language or default to 'en'
    // Ensure the saved language is one of the selectable languages
    if (selectableLanguages.includes(savedLanguage)) {
      return savedLanguage;
    } else {
      saveLanguageToBrowser('en');
      return 'en';
    }
  });

  // Save the selected language to localStorage
  const saveLanguageToBrowser = (language: string) => {
    localStorage.setItem('mrs2-selectedLanguage', language);
  };

  const handleButtonClick = (language: string) => {
    setSelectedLanguage(language); // Update local state
    saveLanguageToBrowser(language); // Save to localStorage
    setContextLanguage(language); // Update the context

    //Refresh the page to apply the new language
    window.location.reload();
  };

  useEffect(() => {
    // Sync the local state with the context when the context changes
    if (contextLanguage !== selectedLanguage) {
      setSelectedLanguage(contextLanguage);
    }
  }, [contextLanguage, selectedLanguage]);

  useEffect(() => {
    // Ensure the language is saved to localStorage on initial load if not already present
    if (!localStorage.getItem('mrs2-selectedLanguage')) {
      saveLanguageToBrowser('en');
    }
  }, []);

  return (
    <Popover className={'relative'}>
      {({ open }) => (
        <>
          <Popover.Button
            className={clsx(
              'keyboard-focus inline-flex items-center gap-x-1 rounded-md border p-2 text-sm font-semibold leading-6',
              'text-gray-900 outline-none dark:border-gray-500 dark:text-gray-100'
            )}
          >
            <span className={'text-sm font-bold sm:text-lg lg:text-xl'}>
              <img
                src={languageFlags[selectedLanguage as 'en' | 'fr']}
                alt={selectedLanguage}
                className="inline-block h-5 w-5 mb-1"
              />
            </span>
            <ChevronDownIcon
              className={clsx(
                'mt-1 h-6 w-6',
                open ? 'rotate-180 transform transition-transform duration-300' : 'duration-300'
              )}
              aria-hidden={'true'}
            />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter={'transition ease-out duration-200'}
            enterFrom={'opacity-0 translate-y-1'}
            enterTo={'opacity-100 translate-y-0'}
            leave={'transition ease-in duration-150'}
            leaveFrom={'opacity-100 translate-y-0'}
            leaveTo={'opacity-0 translate-y-1'}
          >
            <Popover.Panel
              className={'absolute left-28 z-30 mt-1 flex w-screen max-w-min -translate-x-1/2 px-4'}
            >
              <div
                className={clsx(
                  'w-56 shrink rounded-xl bg-white p-4 text-sm font-semibold leading-6',
                  'text-gray-900 shadow-lg ring-1 ring-gray-900/5 dark:bg-gray-600'
                )}
              >
                {selectableLanguages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => handleButtonClick(lang)}
                    className={clsx(
                      'flex w-full items-center gap-2 p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700',
                      selectedLanguage === lang
                        ? 'font-bold text-indigo-600 dark:text-indigo-300'
                        : 'text-gray-900 dark:text-gray-100'
                    )}
                  >
                    <img
                      src={languageFlags[lang as 'en' | 'fr']}
                      alt={lang}
                      className="inline-block h-5 w-5 mr-2"
                    />
                    <span>{languageLabels[lang as 'en' | 'fr']}</span>
                  </button>
                ))}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default LanguageSelector;